/**
 * 后台健康检查服务
 * 定期ping Render服务器以保持其唤醒状态
 */

import BackgroundTimer from 'react-native-background-timer';
import { AppState, AppStateStatus } from 'react-native';
import { API_BASE_URL } from '../config/api';

interface HealthCheckService {
  isRunning: boolean;
  lastPing: Date | null;
  failureCount: number;
}

class BackgroundHealthCheckService implements HealthCheckService {
  isRunning = false;
  lastPing: Date | null = null;
  failureCount = 0;
  private intervalId: number | null = null;
  private appState = AppState.currentState;
  private appStateSubscription: any = null;

  constructor() {
    // 使用新的 AppState.addEventListener API
    this.appStateSubscription = AppState.addEventListener('change', this.handleAppStateChange);
  }

  /**
   * 启动后台健康检查服务
   */
  start(): void {
    if (this.isRunning) {
      console.log('🔄 Health check service is already running');
      return;
    }

    console.log('🚀 Starting background health check service');

    // 使用BackgroundTimer来实现后台任务
    this.intervalId = BackgroundTimer.setInterval(() => {
      this.performHealthCheck();
    }, 5 * 60 * 1000); // 5分钟

    // 立即执行一次健康检查
    this.performHealthCheck();

    this.isRunning = true;
  }

  /**
   * 停止后台健康检查服务
   */
  stop(): void {
    if (!this.isRunning) {
      console.log('⏹️ Health check service is not running');
      return;
    }

    console.log('⏹️ Stopping background health check service');

    if (this.intervalId) {
      BackgroundTimer.clearInterval(this.intervalId);
      this.intervalId = null;
    }

    this.isRunning = false;
  }

  /**
   * 清理资源
   */
  cleanup(): void {
    this.stop();

    // 移除AppState监听器
    if (this.appStateSubscription) {
      this.appStateSubscription.remove();
      this.appStateSubscription = null;
    }
  }

  /**
   * 处理应用状态变化
   */
  private handleAppStateChange = (nextAppState: AppStateStatus) => {
    if (this.appState.match(/inactive|background/) && nextAppState === 'active') {
      // 应用从后台回到前台时，立即执行一次健康检查
      console.log('📱 App became active, performing health check');
      this.performHealthCheck();
    }
    this.appState = nextAppState;
  };

  /**
   * 执行健康检查
   */
  private async performHealthCheck(): Promise<void> {
    try {
      console.log('🏥 Performing health check ping to:', API_BASE_URL);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15秒超时

      const response = await fetch(`${API_BASE_URL}/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'User-Agent': 'ChatFluent-HealthCheck/1.2.0',
          'X-Health-Check': 'background-service',
        },
        signal: controller.signal,
        mode: 'cors',
        credentials: 'omit',
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();

        // 检查服务器返回的status字段
        if (data.status === 'healthy') {
          this.lastPing = new Date();
          this.failureCount = 0;
          console.log('✅ Health check successful:', {
            httpStatus: response.status,
            service: data.service || 'Unknown',
            serverStatus: data.status,
            serverTimestamp: data.timestamp,
            localTimestamp: this.lastPing.toISOString(),
          });
        } else {
          throw new Error(`Server reported unhealthy status: ${data.status}`);
        }
      } else {
        throw new Error(`Health check failed with HTTP status: ${response.status}`);
      }
    } catch (error) {
      this.failureCount++;
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      console.warn('⚠️ Health check failed:', {
        error: errorMessage,
        failureCount: this.failureCount,
        timestamp: new Date().toISOString(),
        baseUrl: API_BASE_URL,
      });

      // 如果连续失败次数过多，记录更详细的错误信息
      if (this.failureCount >= 3) {
        console.error('❌ Health check failed multiple times:', {
          consecutiveFailures: this.failureCount,
          lastError: errorMessage,
          serverUrl: API_BASE_URL,
          suggestion: 'Server might be down or network issues',
        });
      }

      // 失败超过5次时暂时停止（避免无意义的网络请求）
      if (this.failureCount >= 10) {
        console.error('🛑 Too many consecutive failures, temporarily stopping health checks');
        this.stop();

        // 30秒后重新启动
        BackgroundTimer.setTimeout(() => {
          console.log('🔄 Restarting health check service after cooldown');
          // 注意：不重置failureCount，让UI正确显示连续失败状态
          this.start();
        }, 30 * 1000);
      }
    }
  }

  /**
   * 获取服务状态
   */
  getStatus(): HealthCheckService {
    return {
      isRunning: this.isRunning,
      lastPing: this.lastPing,
      failureCount: this.failureCount,
    };
  }

  /**
   * 手动触发健康检查
   */
  async pingNow(): Promise<void> {
    await this.performHealthCheck();
  }
}

// 创建单例实例
const backgroundHealthCheck = new BackgroundHealthCheckService();

export default backgroundHealthCheck;

// 导出类型
export type { HealthCheckService };
